// @mui
import { useTheme } from '@mui/material/styles';
import {
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Alert,
  Button,
  AlertColor,
} from '@mui/material';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// config
import { DEFAULT_HEADER } from '../../../config';
import { useLayout } from 'src/contexts/LayoutContext';
// components
import Logo from '../../../components/logo';
import Iconify from '../../../components/iconify';
import { useSettingsContext } from 'src/components/settings/utils';
//
import { AccountPopover } from 'src/sections/main/header/account';
import { ConversationalTextingPopover } from 'src/sections/main/header/conversational-texting';
import { HeadlessNotificationsPopover } from 'src/sections/main/header/notifications';
import { useOrg } from 'src/hooks/query/orgHooks';
import { IconButtonAnimate } from 'src/components/animate';
import { useUserRole } from 'src/hooks/useUserRole';
import { roleHasPermission } from 'src/auth/rbac';
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Role } from 'src/@types/role';
import { PATH_DASHBOARD } from 'src/routes/paths';
import VoterLookupButton from 'src/sections/main/header/voter-lookup/VoterLookupButton';
import { mixpanelTrack } from 'src/utils/analytics';
import { OrgStatus } from 'src/@types/org';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();

  const [orgIsActive, setOrgIsActive] = useState(false);

  const { data: org, isSuccess: orgLoaded } = useOrg(null, {
    refetchInterval: !orgIsActive ? 60000 : false,
  });

  useEffect(() => {
    if (org) setOrgIsActive(org.status === OrgStatus.Active || false);
  }, [org]);

  const { data: userRole } = useUserRole();

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const { headerLayout, setHeaderLayout: setHeader, navLayout } = useLayout();

  const [alerts, setAlerts] = useState<GlobalAlert[]>([]);

  type GlobalAlert = {
    key: string;
    message: string;
    action?: ReactNode;
    severity: AlertColor;
    visibleTo?: Role[];
  };

  //setting of alerts
  useEffect(() => {
    const overdueInvoiceVisibleTo = [
      Role.Staff,
      Role.OrgAdmin,
      Role.ParentAdmin,
      Role.Owner,
      Role.NuminarAdmin,
    ];

    const overdueInvoiceAlert: GlobalAlert = {
      key: 'customer-overdue-invoice',
      message:
        'The customer associated with this account has an overdue invoice. Overdue outreach invoices will result in suspension of outreach tools. Overdue subscription invoices will result in account deactivation if not paid within 14 days.',
      severity: 'error',
      visibleTo: overdueInvoiceVisibleTo,
    };

    // disabled org alert
    const disabledAlert: GlobalAlert = {
      key: 'org-disabled',
      message: 'This organization is disabled.',
      visibleTo: [Role.NuminarAdmin],
      severity: 'error',
      action: (
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={() => navigate(PATH_DASHBOARD.settings.root + '#admin')}
        >
          Open Admin Settings
        </Button>
      ),
    };

    // pending verification
    const defaultMessage = 'Account verification is pending. Voter Data is not yet available.';
    const dtMessage = 'Account creation is in progress. This feature will be available shortly.';
    const numinarAdminMessage =
      'The account is pending approval. Go to the Campaigns Page of a Numinar Admin Org to approve it.';

    const pendingAlert: GlobalAlert = {
      key: 'org-pending',
      severity: 'warning',
      message:
        userRole && userRole === Role.NuminarAdmin
          ? numinarAdminMessage
          : org?.managed_by_datatrust
            ? dtMessage
            : defaultMessage, // update this to say something about data getting re-loaded / MV, DT message
    };

    const nextAlerts: GlobalAlert[] = [];

    if (org?.invoice_overdue && userRole && overdueInvoiceVisibleTo.includes(userRole)) {
      nextAlerts.push(overdueInvoiceAlert);
    }

    if (org?.is_disabled) {
      nextAlerts.push(disabledAlert);
    }

    if (org && org.status !== OrgStatus.Active) {
      nextAlerts.push(pendingAlert);
    }

    setAlerts((prev) =>
      prev.length === nextAlerts.length &&
      prev.every((alert, index) => alert.key === nextAlerts[index].key)
        ? prev
        : nextAlerts
    );
  }, [org, navigate, userRole]);

  // setting of spacing depending on alerts
  useEffect(() => {
    const alertSpacing = 50 * alerts.length;

    setHeader({
      H_MOBILE: DEFAULT_HEADER.H_MOBILE + alertSpacing,
      H_MAIN_DESKTOP: DEFAULT_HEADER.H_MAIN_DESKTOP + alertSpacing,
      H_DASHBOARD_DESKTOP: DEFAULT_HEADER.H_DASHBOARD_DESKTOP + alertSpacing,
      H_DASHBOARD_DESKTOP_OFFSET: DEFAULT_HEADER.H_DASHBOARD_DESKTOP_OFFSET + alertSpacing,
    });
  }, [alerts, setHeader]);

  const isOffset = useOffSetTop(headerLayout.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const canSearchVoters = roleHasPermission('voter-search', userRole);

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      <Stack flexGrow={1} direction="column">
        <Stack direction="row" flexGrow={1}>
          {!isDesktop && (
            <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary' }}>
              <Iconify icon="eva:menu-2-fill" />
            </IconButton>
          )}
          {!!orgLoaded && org?.status === OrgStatus.Active && canSearchVoters && (
            <VoterLookupButton />
          )}

          <Stack flexGrow={1} direction="row" alignItems="center" justifyContent="flex-end" gap={1}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mr: 1 }}>
              {!!orgLoaded && <ConversationalTextingPopover />}
              <Tooltip
                title="Help Center"
                arrow
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -8],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButtonAnimate
                  onClick={() => {
                    window.open('https://intercom.help/numinar/en/', '_blank');
                    mixpanelTrack('Opened Help Center from Header');
                  }}
                >
                  <Iconify icon="solar:question-circle-bold" />
                </IconButtonAnimate>
              </Tooltip>
              <HeadlessNotificationsPopover />
            </Stack>
            <AccountPopover />
          </Stack>
        </Stack>
        {!!alerts.length &&
          alerts
            .filter((alert) => !alert.visibleTo || (userRole && alert.visibleTo.includes(userRole)))
            .map((alert) => (
              <Alert key={alert.key} severity={alert.severity} sx={{ mt: 1 }} action={alert.action}>
                {alert.message}
              </Alert>
            ))}
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: headerLayout.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${navLayout.W_DASHBOARD + 1}px)`,
          height: headerLayout.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: headerLayout.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: headerLayout.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${navLayout.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
